import { memo, useEffect, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import Skeleton from '@material-ui/lab/Skeleton';
import { useQueryClient } from '@tanstack/react-query';
import NoDataComponent from 'components/NoDataComponent';

import { SEARCH_TAG_TYPES, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { RabbitListContainerAgGrid } from './RabbitListContainerAgGrid';
import { useGetTeamDataQuery } from './hooks/dataHook';

const HorizontalScrollableContainer = styled(Flex)`
  height: 560px;
  margin-bottom: 1rem !important;

  overflow-x: scroll;
  overflow-y: hidden;
  gap: 24px;
`;

const EmployeeCardContainer = styled(Box)`
  height: 100%;
  min-width: 600px;
  max-width: 600px;
`;

const StyledNoDataComponent = styled(NoDataComponent)`
  font-size: 24px;
`;

const emptyStats = {
  delayed: 0,
  ongoing: 0,
  unplanned: 0,
  completed: 0,
  highProgress: 0,
  mediumProgress: 0,
  lowProgress: 0,
  onTrackProgress: 0,
  offTrackProgress: 0,
  atRiskProgress: 0,
  closedProgress: 0,
};

const tagType = SEARCH_TAG_TYPES.team;
const type = TASK_TYPES_2.goal;

function TeamTasksCardView({
  tagId,
  durationKey,
  style = { display: 'flex' },
  activeFilters,
  onEmployeeListUpdate,
  startTS,
  endTS,
  timeBoundPeriod = null,
  selectedCohort = null,
  refreshData,
}) {
  const queryClient = useQueryClient();
  const limit = 5;

  const { data, isLoading } = useGetTeamDataQuery({
    durationKey,
    limit,
    filters: activeFilters,
    tagType,
    tagId,
    type,
    startTS,
    endTS,
    timeBoundPeriod,
    selectedCohort,
  });

  const teamData = useMemo(() => data || { team: {}, tasks: {}, stats: {} }, [data]);

  useEffect(() => {
    if (!isLoading) {
      if (onEmployeeListUpdate && teamData && teamData.teamOrder) {
        onEmployeeListUpdate(teamData.teamOrder);
      }
    }
  }, [teamData, isLoading, onEmployeeListUpdate]);

  const employeeIds = teamData.teamOrder || [];

  useEffect(() => {
    if (refreshData) {
      queryClient.invalidateQueries(REACT_QUERY_KEYS.goalsCardView);
    }
  }, [queryClient, refreshData]);

  if (isLoading) {
    return <Skeleton height="40vh" style={style} />;
  }

  if (employeeIds.length === 0) {
    return <StyledNoDataComponent height="13vh" message="No Data to display" style={style} />;
  }

  return (
    <HorizontalScrollableContainer>
      {employeeIds.map((employeeId) => (
        <EmployeeCardContainer key={employeeId}>
          <RabbitListContainerAgGrid
            stats={teamData.stats[employeeId] || emptyStats}
            employee={teamData.team[employeeId]}
            type={type}
            source="teamPage"
            timeBoundPeriod={timeBoundPeriod}
            selectedCohort={selectedCohort}
          />
        </EmployeeCardContainer>
      ))}
    </HorizontalScrollableContainer>
  );
}

export default memo(TeamTasksCardView);
